/**
 * Game setup module.
 *
 * Manages the pre-game setup phase:
 * - Choosing player count (when multiple options available)
 * - Assigning mates to player slots
 * - Starting games (directly or after setup)
 */

import type { RoomStorage, BroadcastChannel } from '../../types/infrastructure.types'
import type { ChallengeConfiguration, GameCatalogClient } from '../../types/room.types'
import type { GameSetup, GameSetupPhase, SetupReadiness } from '../../types/gameSetup.types'
import type { MateInRoom } from '../../types/types'
import { unseatMates } from '../hosting/hosting'
import type {
  OpenGameArgs,
  ChoosePlayerCountArgs,
  UpdateMateAssignmentArgs,
  StartGameArgs,
} from '../../schemas/action.schemas'

/**
 * Interface for starting games. Provided by gameInRoom module.
 */
export interface GameStarter {
  startGame(args: StartGameArgs): Promise<{ success: boolean; error?: string }>
}

/**
 * Interface for getting effective game options. Provided by gameOptions module.
 */
export interface EffectiveOptionsProvider {
  getEffectiveOptions(gameId: string, playerCount: number): Promise<Record<string, unknown>>
}

export interface GameSetupContainer {
  storage: RoomStorage
  broadcastChannel: BroadcastChannel
  gameCatalog: GameCatalogClient
  /** For starting games directly when no setup is needed */
  gameStarter?: GameStarter
  /** For getting effective options when starting games */
  optionsProvider?: EffectiveOptionsProvider
  challengeConfigurationPreferenceProvider?: { getChallengeConfigurationPreference(args: { gameId: string }): Promise<{ challengeConfiguration: ChallengeConfiguration | null }> }
  /** Mates eligible for player seats in a game under its Host plays setting. */
  seatEligibility: { eligibleMates(gameId: string): Promise<MateInRoom[]> }
}

/**
 * Get min/max players for a game from registry metadata.
 */
async function getPlayerBounds(gameId: string, channel: string, gameCatalog: GameCatalogClient): Promise<{ minPlayers: number; maxPlayers: number } | null> {
  const version = await gameCatalog.resolveVersion(gameId, channel)
  if (!version) return null
  return { minPlayers: version.metadata.minPlayers, maxPlayers: version.metadata.maxPlayers }
}

/**
 * Compute available player count options for a game given the current mate count.
 */
async function computeSlotOptions(
  gameId: string,
  channel: string,
  gameCatalog: GameCatalogClient,
  mateCount: number,
): Promise<number[] | null> {
  const bounds = await getPlayerBounds(gameId, channel, gameCatalog)
  if (!bounds) return null
  const { minPlayers, maxPlayers } = bounds

  const options: number[] = []
  for (let n = minPlayers; n <= maxPlayers && n <= mateCount; n++) {
    options.push(n)
  }
  return options
}

/**
 * Determine which phase to start in (or null if no setup needed).
 */
function determineInitialPhase(
  slotOptions: number[],
  mateCount: number
): { phase: GameSetupPhase | null; slotCount: number | null } {
  if (slotOptions.length === 0) {
    return { phase: null, slotCount: null }
  }

  if (slotOptions.length === 1) {
    const onlyOption = slotOptions[0]
    if (mateCount === onlyOption) {
      // Perfect fit: no setup needed
      return { phase: null, slotCount: onlyOption }
    }
    // More mates than slots: need to assign
    return { phase: 'assignSlots', slotCount: onlyOption }
  }

  // Multiple options: need to choose player count
  return { phase: 'choosePlayerCount', slotCount: null }
}

/**
 * Compute setup readiness based on current state.
 */
function computeReadiness(setup: GameSetup, mates: MateInRoom[]): SetupReadiness {
  const missingConditions: string[] = []

  if (setup.phase === 'choosePlayerCount') {
    missingConditions.push('Choose number of players')
    return { canStart: false, missingConditions }
  }

  // Phase: assignSlots
  const slotCount = setup.slotCount!
  const slotCounts: number[] = Array(slotCount).fill(0)
  const assignedMateIds = new Set<string>()

  for (const [mateId, slotIndex] of Object.entries(setup.assignments)) {
    slotCounts[slotIndex]++
    assignedMateIds.add(mateId)
  }

  // Check each slot has at least one mate
  for (let i = 0; i < slotCount; i++) {
    if (slotCounts[i] === 0) {
      missingConditions.push(`Slot ${i + 1} needs at least one mate`)
    }
  }

  // Info about unassigned mates (not blocking, just informational)
  const unassignedCount = mates.length - assignedMateIds.size
  if (unassignedCount > 0) {
    missingConditions.push(`${unassignedCount} mate(s) will be observers`)
  }

  const slotsBlocking = missingConditions.filter((c) => c.includes('needs')).length
  return { canStart: slotsBlocking === 0, missingConditions }
}

export type OpenGameOutcome = 'gameStarted' | 'setupStarted'
export type ChoosePlayerCountOutcome = 'gameStarted' | 'movedToAssignSlots'

export interface GameSetupModule {
  /**
   * Open a game for setup or start it directly.
   *
   * If gameStarter and optionsProvider are provided in the container,
   * this will start the game directly when no setup is needed.
   * Otherwise, it returns data for the caller to start the game.
   */
  openGame(args: OpenGameArgs): Promise<{
    success: boolean
    error?: string
    outcome?: OpenGameOutcome
    setup?: GameSetup
    readiness?: SetupReadiness
  }>

  /**
   * Choose player count during setup.
   *
   * If mate count matches chosen count and gameStarter is available,
   * starts the game directly.
   */
  choosePlayerCount(args: ChoosePlayerCountArgs): Promise<{
    success: boolean
    error?: string
    outcome?: ChoosePlayerCountOutcome
    setup?: GameSetup
    readiness?: SetupReadiness
  }>

  updateMateAssignment(args: UpdateMateAssignmentArgs): Promise<{
    success: boolean
    error?: string
    setup?: GameSetup
    readiness?: SetupReadiness
  }>

  /**
   * Start the game from the current setup configuration.
   *
   * Requires gameStarter and optionsProvider to be provided in the container.
   */
  startGameFromSetup(): Promise<{
    success: boolean
    error?: string
  }>

  cancelGameSetup(): Promise<{ success: boolean }>

  getGameSetup(): Promise<{ setup: GameSetup; readiness: SetupReadiness } | null>
}

/**
 * Explains a refused start to the person who chose the game, counting the
 * mates who observe it (such as hosts under Host plays) so it's clear why
 * a room that looks full enough can't start.
 */
function tooFewPlayersError(minPlayers: number, playableCount: number, observingCount: number): string {
  const playable = playableCount === 1 ? '1 person' : `${playableCount} people`
  const observing = observingCount > 0 ? ` (${observingCount} more ${observingCount === 1 ? 'observes' : 'observe'})` : ''
  return `This game needs at least ${minPlayers} players, but only ${playable} in the room can play${observing}.`
}

export function makeGameSetup(container: GameSetupContainer): GameSetupModule {
  const { storage, broadcastChannel, gameCatalog, gameStarter, optionsProvider, challengeConfigurationPreferenceProvider, seatEligibility } = container
  const { eligibleMates } = seatEligibility

  /**
   * Helper to start a game with effective options.
   */
  const startGameWithOptions = async (
    gameId: string,
    options: {
      mateIDs?: string[]
      mateGroups?: string[][]
      observerIDs?: string[]
      randomizeSlotAssignment?: boolean
      challengeConfiguration?: ChallengeConfiguration
    }
  ): Promise<{ success: boolean; error?: string }> => {
    if (!gameStarter) {
      return { success: false, error: 'Game starter not available' }
    }

    // Get effective options
    const slotCount = options.mateGroups?.length ?? options.mateIDs?.length ?? 2
    const gameOptions = optionsProvider
      ? await optionsProvider.getEffectiveOptions(gameId, slotCount)
      : {}

    return gameStarter.startGame({
      gameId,
      ...options,
      gameOptions: Object.keys(gameOptions).length > 0 ? gameOptions : undefined,
    })
  }

  const openGame: GameSetupModule['openGame'] = async (args) => {
    const { gameId, clientId, randomize } = args
    const state = storage.getState()
    const shouldRandomize = randomize ?? true // Default to true for fairness

    if (state.activeGame) {
      return { success: false, error: 'A game is already in progress' }
    }
    if (state.gameSetup) {
      return { success: false, error: 'A setup is already in progress' }
    }

    const seatMates = await eligibleMates(gameId)
    const mateCount = seatMates.length
    const channel = state.gameChannelPreferenceById?.[gameId] ?? 'stable'
    const version = await gameCatalog.resolveVersion(gameId, channel)
    if (!version) return { success: false, error: `No published game found for ${gameId}` }

    const supportsChallengeConfiguration = version.metadata.challengeSources !== undefined
    const gameChallengeArrangements = version.metadata.gameChallengeArrangements ?? []
    const opponentChallengeSupported = version.metadata.challengeSources?.includes('opponent') === true
    const gameChallengeSupported = version.metadata.challengeSources?.includes('game') === true
    const preferredConfiguration = (await challengeConfigurationPreferenceProvider?.getChallengeConfigurationPreference({ gameId }))?.challengeConfiguration
    const defaultConfiguration: ChallengeConfiguration = opponentChallengeSupported && mateCount >= 2 && mateCount <= version.metadata.maxPlayers
      ? { challengeSource: 'opponent' }
      : { challengeSource: 'game', gameChallengeArrangement: 'cooperative' }
    const preferredConfigurationSupported = preferredConfiguration?.challengeSource === 'opponent'
      ? opponentChallengeSupported && mateCount >= 2
      : preferredConfiguration?.challengeSource === 'game'
        ? gameChallengeSupported
          && gameChallengeArrangements.includes(preferredConfiguration.gameChallengeArrangement)
          && (preferredConfiguration.gameChallengeArrangement === 'cooperative' || mateCount >= 2)
        : false
    const challengeConfiguration = preferredConfiguration && preferredConfigurationSupported
      ? preferredConfiguration
      : defaultConfiguration

    if (supportsChallengeConfiguration && challengeConfiguration.challengeSource === 'game' && challengeConfiguration.gameChallengeArrangement === 'cooperative' && mateCount > 0) {
      const result = await startGameWithOptions(gameId, {
        mateGroups: [seatMates.map((mate) => mate.mateId)],
        challengeConfiguration,
      })
      return { success: result.success, error: result.error, outcome: result.success ? 'gameStarted' : undefined }
    }

    if (supportsChallengeConfiguration && challengeConfiguration.challengeSource === 'game' && (challengeConfiguration.gameChallengeArrangement === 'sameChallengeRace' || challengeConfiguration.gameChallengeArrangement === 'differentChallengeRace') && mateCount >= 2) {
      const result = await startGameWithOptions(gameId, {
        mateIDs: seatMates.map((mate) => mate.mateId),
        randomizeSlotAssignment: shouldRandomize,
        challengeConfiguration,
      })
      return { success: result.success, error: result.error, outcome: result.success ? 'gameStarted' : undefined }
    }

    if (supportsChallengeConfiguration && challengeConfiguration.challengeSource === 'opponent' && mateCount >= version.metadata.minPlayers && mateCount <= version.metadata.maxPlayers) {
      const result = await startGameWithOptions(gameId, {
        mateIDs: seatMates.map((mate) => mate.mateId),
        randomizeSlotAssignment: shouldRandomize,
        challengeConfiguration,
      })
      return { success: result.success, error: result.error, outcome: result.success ? 'gameStarted' : undefined }
    }

    const slotOptions = await computeSlotOptions(gameId, channel, gameCatalog, mateCount)

    if (!slotOptions) return { success: false, error: `No published game found for ${gameId}` }

    if (slotOptions.length === 0) {
      const bounds = await getPlayerBounds(gameId, channel, gameCatalog)
      const minPlayers = bounds?.minPlayers ?? 2
      return { success: false, error: tooFewPlayersError(minPlayers, mateCount, state.mates.length - mateCount) }
    }

    let { phase, slotCount } = determineInitialPhase(slotOptions, mateCount)
    if (gameId === 'new-game') {
      phase = null
      slotCount = 1
    }

    // No setup needed: start directly
    if (phase === null && slotCount !== null) {
      let mateIDs = seatMates.map((m) => m.mateId)
      if (gameId === 'new-game') {
        const initiatorMate = state.mates.find((m) => m.clientId === clientId)
        mateIDs = initiatorMate ? [initiatorMate.mateId] : [state.mates[0]?.mateId].filter((id): id is string => !!id)
      }

      // If we have a game starter, start the game directly
      if (gameStarter) {
        const result = await startGameWithOptions(gameId, {
          mateIDs,
          randomizeSlotAssignment: randomize,
        })
        return {
          success: result.success,
          error: result.error,
          outcome: 'gameStarted',
        }
      }

      // No game starter: return data for caller to start (legacy path)
      return {
        success: true,
        outcome: 'gameStarted',
      }
    }

    // Create setup state
    const setup: GameSetup = {
      gameId,
      phase: phase!,
      slotCountOptions: slotOptions,
      slotCount,
      assignments: {},
      seatEligibleMateIds: seatMates.map((m) => m.mateId),
      startedAt: new Date().toISOString(),
      initiatorClientId: clientId,
      randomize: shouldRandomize,
    }

    const readiness = computeReadiness(setup, state.mates)

    storage.setState({ ...state, gameSetup: setup })
    broadcastChannel.broadcast({ type: 'setupStarted', data: { setup, readiness } })

    return { success: true, outcome: 'setupStarted', setup, readiness }
  }

  const choosePlayerCount: GameSetupModule['choosePlayerCount'] = async (args) => {
    const { count } = args
    const state = storage.getState()

    if (!state.gameSetup) {
      return { success: false, error: 'No setup in progress' }
    }
    if (state.gameSetup.phase !== 'choosePlayerCount') {
      return { success: false, error: 'Not in player count selection phase' }
    }
    if (!state.gameSetup.slotCountOptions.includes(count)) {
      return { success: false, error: 'Invalid player count' }
    }

    const seatMates = await eligibleMates(state.gameSetup.gameId)
    const mateCount = seatMates.length
    const gameId = state.gameSetup.gameId
    const randomize = state.gameSetup.randomize

    // If mate count matches chosen count, start immediately
    if (mateCount === count) {
      const mateIDs = seatMates.map((m) => m.mateId)

      storage.setState({ ...state, gameSetup: null })

      // If we have a game starter, start the game directly
      if (gameStarter) {
        const result = await startGameWithOptions(gameId, {
          mateIDs,
          randomizeSlotAssignment: randomize,
        })
        return {
          success: result.success,
          error: result.error,
          outcome: 'gameStarted',
        }
      }

      // No game starter: return success (legacy path)
      return {
        success: true,
        outcome: 'gameStarted',
      }
    }

    // Move to slot assignment phase
    const setup: GameSetup = {
      ...state.gameSetup,
      phase: 'assignSlots',
      slotCount: count,
      assignments: {},
    }

    const readiness = computeReadiness(setup, state.mates)

    storage.setState({ ...state, gameSetup: setup })
    broadcastChannel.broadcast({ type: 'setupChanged', data: { setup, readiness } })

    return { success: true, outcome: 'movedToAssignSlots', setup, readiness }
  }

  const updateMateAssignment: GameSetupModule['updateMateAssignment'] = async (args) => {
    const { mateId, slotIndex, clientId } = args
    const state = storage.getState()

    if (!state.gameSetup) {
      return { success: false, error: 'No setup in progress' }
    }
    if (state.gameSetup.phase !== 'assignSlots') {
      return { success: false, error: 'Not in slot assignment phase' }
    }

    const mate = state.mates.find((m) => m.mateId === mateId)
    if (!mate) {
      return { success: false, error: 'Mate not found' }
    }

    if (mate.clientId !== clientId) {
      return { success: false, error: "Cannot assign another client's mate" }
    }

    if (slotIndex !== null && !(await eligibleMates(state.gameSetup.gameId)).some((m) => m.mateId === mateId)) {
      return { success: false, error: 'Hosts observe under this game\'s Host plays setting' }
    }

    if (slotIndex !== null) {
      if (slotIndex < 0 || slotIndex >= state.gameSetup.slotCount!) {
        return { success: false, error: 'Invalid slot index' }
      }
    }

    const newAssignments = { ...state.gameSetup.assignments }
    if (slotIndex === null) {
      delete newAssignments[mateId]
    } else {
      newAssignments[mateId] = slotIndex
    }

    const setup: GameSetup = { ...state.gameSetup, assignments: newAssignments }
    const readiness = computeReadiness(setup, state.mates)

    storage.setState({ ...state, gameSetup: setup })
    broadcastChannel.broadcast({ type: 'setupChanged', data: { setup, readiness } })

    return { success: true, setup, readiness }
  }

  const startGameFromSetup: GameSetupModule['startGameFromSetup'] = async () => {
    const state = storage.getState()

    if (!state.gameSetup) {
      return { success: false, error: 'No setup in progress' }
    }
    if (state.gameSetup.phase !== 'assignSlots') {
      return { success: false, error: 'Must complete setup before starting' }
    }

    const readiness = computeReadiness(state.gameSetup, state.mates)
    if (!readiness.canStart) {
      const blocking = readiness.missingConditions.filter((c) => c.includes('needs'))
      return { success: false, error: `Cannot start: ${blocking.join(', ')}` }
    }

    // Build mateGroups from assignments
    const slotCount = state.gameSetup.slotCount!
    const mateGroups: string[][] = Array.from({ length: slotCount }, () => [])
    const observerIds: string[] = []

    const assignedMateIds = new Set(Object.keys(state.gameSetup.assignments))
    for (const [mateId, slot] of Object.entries(state.gameSetup.assignments)) {
      mateGroups[slot].push(mateId)
    }
    for (const mate of state.mates) {
      if (!assignedMateIds.has(mate.mateId)) {
        observerIds.push(mate.mateId)
      }
    }

    const gameId = state.gameSetup.gameId
    const randomize = state.gameSetup.randomize

    // Host status or managed mode may have changed since mates were assigned
    const eligibleIds = new Set((await eligibleMates(gameId)).map((m) => m.mateId))
    const seats = unseatMates(
      { mateGroups, observerIDs: observerIds },
      new Set(state.mates.map((m) => m.mateId).filter((id) => !eligibleIds.has(id))),
    )
    if (!seats) {
      return { success: false, error: 'A seat has only hosts who observe under this game\'s Host plays setting' }
    }

    // Clear setup
    storage.setState({ ...state, gameSetup: null })

    // Start the game
    if (!gameStarter) {
      return { success: false, error: 'Game starter not available' }
    }

    const result = await startGameWithOptions(gameId, {
      ...seats,
      randomizeSlotAssignment: randomize,
    })

    return {
      success: result.success,
      error: result.error,
    }
  }

  const cancelGameSetup: GameSetupModule['cancelGameSetup'] = async () => {
    const state = storage.getState()
    if (state.gameSetup) {
      storage.setState({ ...state, gameSetup: null })
      broadcastChannel.broadcast({ type: 'setupCancelled' })
    }
    return { success: true }
  }

  const getGameSetup: GameSetupModule['getGameSetup'] = async () => {
    const state = storage.getState()
    const setup = state.gameSetup
    if (!setup) return null

    const readiness = computeReadiness(setup, state.mates)
    return { setup, readiness }
  }

  return {
    openGame,
    choosePlayerCount,
    updateMateAssignment,
    startGameFromSetup,
    cancelGameSetup,
    getGameSetup,
  }
}
